import { useEffect, useMemo, useState } from 'react';
import { api, errorMessage } from '../api';
import { dateTime, timeAgo, uptime, COMPANY_STATUS, MEMBER_STATUS, ROLE_LABEL } from '../format';
import { FIELD_LABELS } from '../types';
import type { Contribution, ServerStatus, Session } from '../types';
import { Badge, Btn, Card, CardTitle, Empty, Loading, Modal, Notice, SecHead, Stat, TextArea } from '../components/ui';
import { CodeModal } from './Company';
import { usePortal } from '../App';

/** Panel del administrador del directorio, por secciones del riel: Miembros
    (cola de acceso + equipo), Aportaciones, Empresas, Bitácora y Servidor
    (salud del servicio en vivo). Todo lo que la app ofrece en «Miembros» +
    «Estado del servidor», pensado para pantalla grande. */
export function AdminView({ section }: { section: string }) {
  const { token, data, busy, mutate, refresh, session, authFail } = usePortal();
  const [status, setStatus] = useState<ServerStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<Session | null>(null);
  const [reviewing, setReviewing] = useState<Contribution | null>(null);
  const [codeShown, setCodeShown] = useState<{ company: string; code: string } | null>(null);
  const [digestMsg, setDigestMsg] = useState<string | null>(null);

  const loadStatus = async () => {
    try { setStatus(await api<ServerStatus>('serverStatus', {}, token)); setStatusError(null); }
    catch (e) { if (authFail(e)) return; setStatusError(errorMessage(e)); }
  };
  // `data` en deps: tras una mutación (autorizar miembro, empresa…) el panel
  // refresca sus conteos junto al snapshot, que cambia de identidad.
  useEffect(() => { void loadStatus(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token, data]);

  const placeName = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of [...(data?.places ?? []), ...(data?.companyPlaces ?? [])]) map.set(p.id, p.name);
    return (id: string) => map.get(id) ?? 'Lugar no disponible';
  }, [data]);

  if (!data) return <Loading label="Leyendo…" />;

  const pendingMembers = data.members.filter(m => m.status === 'pending');
  const activeMembers = data.members.filter(m => m.status === 'active');
  const blockedMembers = data.members.filter(m => m.status === 'blocked');
  const pendingContribs = data.contributions.filter(c => c.status === 'pending').sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const companies = data.companies ?? [];
  const audit = data.audit ?? [];

  const sendDigest = () => void mutate(async () => {
    const { sent } = await api<{ sent: number }>('emailDigest', {}, token);
    setDigestMsg(sent ? `Resumen enviado a ${sent === 1 ? 'un administrador' : `${sent} administradores`}.` : 'El servidor no tiene correo configurado; el resumen no salió.');
  });

  const rotateCode = (companyId: string, name: string) => {
    if (!confirm(`¿Generar un código nuevo para ${name}? El anterior dejará de funcionar.`)) return;
    void mutate(async () => {
      const { code } = await api<{ code: string }>('rotateCompanyCode', { companyId }, token);
      setCodeShown({ company: name, code });
    });
  };

  const refreshBtn = <Btn tone="secondary" small busy={busy} onClick={() => { void refresh(); void loadStatus(); }}>Actualizar</Btn>;

  return <>
    {section === 'miembros' && <>
      <SecHead title="Miembros" desc="Solicitudes de acceso y equipo." actions={refreshBtn} />

      {/* Cola de acceso: autorizar = setMemberStatus 'active'; rechazar = rejectMember con motivo opcional. */}
      <Card>
        <CardTitle detail="Se revisan en el orden en que llegaron.">Solicitudes de acceso{pendingMembers.length ? ` · ${pendingMembers.length}` : ''}</CardTitle>
        {!pendingMembers.length && <Empty icon="people" title="Todo al día" message="No hay cuentas esperando autorización." />}
        <div className="list">
          {pendingMembers.map(m => (
            <div className="item" key={m.uid}>
              <div className="row between multiline">
                <div>
                  <div className="title">{m.displayName || 'Sin nombre'}</div>
                  <div className="small muted">{m.email}{m.phone ? ` · ${m.phone}` : ''}</div>
                </div>
                <div className="actions">
                  <Btn small busy={busy} onClick={() => void mutate(() => api('setMemberStatus', { uid: m.uid, status: 'active' }, token), 'Acceso del usuario actualizado.')}>Autorizar</Btn>
                  <Btn small tone="danger" busy={busy} onClick={() => setRejecting(m)}>Rechazar</Btn>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Equipo: activos se pueden suspender; suspendidos se reactivan. */}
      <Card>
        <CardTitle detail="Suspender corta el acceso al directorio de inmediato; al reactivar, el miembro vuelve a entrar normal.">Registrados{activeMembers.length + blockedMembers.length ? ` · ${activeMembers.length + blockedMembers.length}` : ''}</CardTitle>
        <div className="list">
          {[...activeMembers, ...blockedMembers].map(m => {
            const self = m.uid === session.uid;
            const st = MEMBER_STATUS[m.status] ?? { label: m.status, tone: 'muted' as const };
            return <div className="item" key={m.uid}>
              <div className="row between multiline">
                <div>
                  <div className="title">{m.displayName || 'Sin nombre'}{self ? ' (tú)' : ''}</div>
                  <div className="small muted">{m.email}{m.phone ? ` · ${m.phone}` : ''}{m.companyName ? ` · ${m.companyName}` : ''}</div>
                </div>
                <div className="actions">
                  <Badge tone="muted">{ROLE_LABEL[m.role] ?? m.role}</Badge>
                  <Badge tone={st.tone}>{st.label}</Badge>
                  {!self && <Btn small tone={m.status === 'active' ? 'danger' : 'secondary'} busy={busy}
                    onClick={() => {
                      const next = m.status === 'active' ? 'blocked' : 'active';
                      if (next === 'blocked' && !confirm(`${m.displayName} perderá el acceso.`)) return;
                      void mutate(() => api('setMemberStatus', { uid: m.uid, status: next }, token), 'Acceso del usuario actualizado.');
                    }}>
                    {m.status === 'active' ? 'Suspender acceso' : 'Autorizar acceso'}
                  </Btn>}
                </div>
              </div>
            </div>;
          })}
        </div>
      </Card>
    </>}

    {section === 'aportaciones' && <>
      <SecHead title="Aportaciones" desc="Cambios que los choferes proponen para las fichas del directorio." actions={refreshBtn} />

      {/* Aportaciones de los choferes: aprobar aplica el cambio a la ficha; rechazar exige nota. */}
      <Card>
        <CardTitle detail="Aprobar actualiza la ficha del lugar de inmediato; rechazar pide un motivo que ve el autor.">Pendientes{pendingContribs.length ? ` · ${pendingContribs.length}` : ''}</CardTitle>
        {!pendingContribs.length && <Empty icon="clipboard" title="Todo al día" message="No hay aportaciones esperando revisión." />}
        <div className="list">
          {pendingContribs.map(c => (
            <div className="item" key={c.id}>
              <div className="row between multiline">
                <div>
                  <div className="title">{c.kind === 'newPlace' ? `Lugar nuevo: ${c.proposedValue}` : `${placeName(c.placeId)} · ${FIELD_LABELS[c.field as keyof typeof FIELD_LABELS] ?? c.field}`}</div>
                  <div className="small muted">{c.authorName || 'Autor desconocido'} · {timeAgo(c.createdAt)}</div>
                </div>
                <Badge tone="warn">Pendiente</Badge>
              </div>
              {c.kind === 'newPlace'
                ? <dl className="kv">
                    <dt>Zona</dt><dd>{c.draft?.zone || '—'}</dd>
                    <dt>Dirección</dt><dd>{c.draft?.address || '—'}</dd>
                    <dt>Horario</dt><dd>{c.draft?.hours || '—'}</dd>
                  </dl>
                : <div className="diff">
                    <span className="from">Actual: {c.currentValue || '—'}</span>
                    <span className="to">Propuesto: {c.proposedValue || '—'}</span>
                  </div>}
              {c.reason && <div className="small muted">Motivo: {c.reason}</div>}
              <div className="actions">
                <Btn small busy={busy} onClick={() => void mutate(() => api('review', { contributionId: c.id, decision: 'approve', note: '' }, token), 'Cambio aprobado y ficha actualizada.')}>Aprobar</Btn>
                <Btn small tone="danger" busy={busy} onClick={() => setReviewing(c)}>Rechazar</Btn>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </>}

    {section === 'empresas' && <>
      <SecHead
        title={<>Empresas{companies.length > 0 && <span className="cnt"> · {companies.length}</span>}</>}
        desc="Espacios privados; operan cuando el administrador las autoriza." actions={refreshBtn} />

      {/* Empresas registradas: el admin las autoriza/suspende y regenera su código de invitación. */}
      <Card>
        <p className="card-detail u-m0">El código de invitación solo se muestra una vez al regenerarlo.</p>
        {!companies.length && <Empty icon="building" title="Sin empresas" message="Aún no hay empresas registradas." />}
        <div className="list">
          {companies.map(co => {
            const st = COMPANY_STATUS[co.status] ?? COMPANY_STATUS.pending;
            return <div className="item" key={co.id}>
              <div className="row between multiline">
                <div>
                  <div className="title">{co.name}</div>
                  <div className="small muted">{co.memberCount} miembro{co.memberCount === 1 ? '' : 's'} · registrada {dateTime(co.createdAt)}</div>
                </div>
                <Badge tone={st.tone}>{st.label}</Badge>
              </div>
              <div className="actions">
                {co.status !== 'active' && <Btn small busy={busy} onClick={() => void mutate(() => api('setCompanyStatus', { companyId: co.id, status: 'active' }, token), 'Empresa autorizada.')}>Autorizar</Btn>}
                {co.status === 'active' && <Btn small tone="danger" busy={busy} onClick={() => {
                  if (!confirm(`¿Suspender «${co.name}»? Dejará de compartir datos privados (lugares, notas y viajes) hasta que la autorices de nuevo. Sus miembros no pierden su cuenta.`)) return;
                  void mutate(() => api('setCompanyStatus', { companyId: co.id, status: 'blocked' }, token), 'Empresa suspendida.');
                }}>Suspender</Btn>}
                <Btn small tone="ghost" busy={busy} onClick={() => rotateCode(co.id, co.name)}>Código nuevo</Btn>
              </div>
            </div>;
          })}
        </div>
      </Card>
    </>}

    {section === 'bitacora' && <>
      <SecHead title="Bitácora" desc="Solicitudes, autorizaciones y cambios recientes." actions={refreshBtn} />

      {/* Bitácora reciente (la recibe solo el admin en el snapshot). */}
      <Card>
        <CardTitle>Actividad reciente{audit.length ? ` · ${audit.length}` : ''}</CardTitle>
        {!audit.length && <Empty icon="list" title="Sin actividad reciente" message="Las solicitudes, autorizaciones y cambios aparecerán aquí." />}
        {audit.length > 0 && <div className="list">
          {audit.slice(0, 30).map(ev => (
            <div className="item" key={ev.id}>
              <div className="row between multiline">
                <span className="small mono muted">{ev.action}</span>
                <span className="tiny muted">{timeAgo(ev.createdAt)}</span>
              </div>
              {ev.note && <div className="small muted">{ev.note}</div>}
            </div>
          ))}
        </div>}
        {/* Solo se renderizan 30: el resto queda contado, como en Unidades. */}
        {audit.length > 30 && <div className="tiny muted">Y {audit.length - 30} más.</div>}
      </Card>
    </>}

    {section === 'servidor' && <>
      <SecHead title="Servidor" desc="Salud del servicio en vivo." actions={<>
        {refreshBtn}
        <Btn tone="secondary" small busy={busy} onClick={sendDigest}>Enviar resumen por correo</Btn>
      </>} />
      {digestMsg && <Notice tone="info" onClose={() => setDigestMsg(null)}>{digestMsg}</Notice>}

      {/* Salud del servicio en vivo (acción serverStatus, solo admin). */}
      <Card>
        <CardTitle detail={status ? `Hora del servidor: ${dateTime(status.now)} · Encendido ${uptime(status.uptimeSec)}${status.version ? ` · v${status.version}` : ''}` : 'Consulta directa al servicio.'}>Estado del servidor</CardTitle>
        {statusError && <Notice tone="warning">{statusError}</Notice>}
        {!status && !statusError && <Loading label="Consultando…" />}
        {status && <div className="grid stats">
          <Stat n={status.counts.membersActive} l="Miembros activos" />
          <Stat n={status.counts.membersPending} l="Por autorizar" warn={status.counts.membersPending > 0} />
          <Stat n={status.counts.members} l="Miembros totales" />
          <Stat n={status.counts.places} l="Lugares" />
          <Stat n={status.counts.visits} l="Visitas" />
          <Stat n={status.counts.contributionsPending} l="Aportes pendientes" warn={status.counts.contributionsPending > 0} />
          <Stat n={status.counts.sessionsActive} l="Sesiones vivas" />
          <Stat n={status.counts.pushTokens} l="Dispositivos push" />
        </div>}
      </Card>
    </>}

    {rejecting && <RejectModal member={rejecting} onClose={() => setRejecting(null)} />}
    {reviewing && <ReviewModal contribution={reviewing} placeName={placeName(reviewing.placeId)} onClose={() => setReviewing(null)} />}
    {codeShown && <CodeModal title="Código de invitación nuevo" code={codeShown.code} onClose={() => setCodeShown(null)}>
      <p className="muted small u-m0">Código para <b>{codeShown.company}</b>. Compártelo con la empresa; <b>se muestra una sola vez</b> y el anterior dejó de funcionar.</p>
    </CodeModal>}
  </>;
}

function RejectModal({ member, onClose }: { member: Session; onClose(): void }) {
  const { token, busy, mutate } = usePortal();
  const [reason, setReason] = useState('');
  return <Modal title="Rechazar solicitud" onClose={onClose}>
    <div><div className="name">{member.displayName || 'Sin nombre'}</div><div className="small muted">{member.email}</div></div>
    <p className="small muted u-m0">{member.displayName} saldrá de la lista y no podrá entrar. Si fue un error, puede crear una cuenta nueva.</p>
    <TextArea label="Motivo (opcional)" value={reason} onChange={e => setReason(e.target.value)} hint="Queda en la bitácora y viaja en el correo de aviso al solicitante." maxLength={500} />
    <Btn tone="danger" busy={busy} onClick={() => {
      void mutate(() => api('rejectMember', { uid: member.uid, ...(reason.trim() ? { reason: reason.trim() } : {}) }, token), 'Solicitud rechazada.')
        .then(ok => { if (ok) onClose(); });
    }}>Rechazar solicitud</Btn>
  </Modal>;
}

function ReviewModal({ contribution, placeName, onClose }: { contribution: Contribution; placeName: string; onClose(): void }) {
  const { token, busy, mutate } = usePortal();
  const [note, setNote] = useState('');
  const valid = note.trim().length >= 3;
  return <Modal title="Rechazar aportación" onClose={onClose}>
    <div>
      <div className="name">{contribution.kind === 'newPlace' ? `Lugar nuevo: ${contribution.proposedValue}` : placeName}</div>
      <div className="small muted">{contribution.authorName} · {FIELD_LABELS[contribution.field as keyof typeof FIELD_LABELS] ?? contribution.field}</div>
    </div>
    <TextArea label="Motivo del rechazo" value={note} onChange={e => setNote(e.target.value)} hint="Obligatorio — el autor lo verá en su historial de aportaciones." maxLength={1000} />
    <Btn tone="danger" busy={busy} disabled={!valid} onClick={() => {
      void mutate(() => api('review', { contributionId: contribution.id, decision: 'reject', note: note.trim() }, token), 'Aporte rechazado con su motivo.')
        .then(ok => { if (ok) onClose(); });
    }}>Rechazar aportación</Btn>
  </Modal>;
}
